import { SafePromise, SafeType } from '@benzinga/safe-await';
import { oneRequestAtATime } from '@benzinga/utils';
import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';

import { AuthenticationManager, StockSymbol } from '@benzinga/session';
import { Session } from '@benzinga/session';
import { Watchlist, WatchlistId, WatchlistSymbol, YodleeAccess } from './entities';
import { WatchlistRequest, WatchlistRequestEvent } from './request';
import { WatchlistStore, WatchlistStoreEvent } from './store';
import { WatchlistEnvironment } from './environment';
import { normalizeWatchlistSymbol } from './ingest';

interface WatchlistFunctions {
  addTickersToWatchlist: WatchlistManager['addTickersToWatchlist'];
  createWatchlist: WatchlistManager['createWatchlist'];
  getStoredWatchlistById: WatchlistManager['getStoredWatchlistById'];
  getStoredWatchlists: WatchlistManager['getStoredWatchlists'];
  getWatchlists: WatchlistManager['getWatchlists'];
  removeTickersFromWatchlist: WatchlistManager['removeTickersFromWatchlist'];
  removeWatchlist: WatchlistManager['removeWatchlist'];
  updateWatchlistSettings: WatchlistManager['updateWatchlistSettings'];
  updateWatchlistTicker: WatchlistManager['updateWatchlistTicker'];
}

export type WatchlistManagerEvent = WatchlistRequestEvent | WatchlistStoreEvent;

export class WatchlistManager extends ExtendedSubscribable<WatchlistManagerEvent, WatchlistFunctions> {
  private store: WatchlistStore;
  private request: WatchlistRequest;
  private storeSubscription?: Subscription<WatchlistStore>;
  private requestSubscription?: Subscription<WatchlistRequest>;
  private authenticationManager: AuthenticationManager;
  private fastLinkUrl: string;
  private fastLinkConfigName: string;
  private fetchWatchlist = oneRequestAtATime(async () => {
    const watchlists = await this.request.getWatchlists();
    if (watchlists.err) {
      return watchlists as SafeType<Watchlist[] | undefined>;
    } else {
      this.store.updateWatchlists(watchlists.ok);
      return watchlists;
    }
  });

  constructor(session: Session) {
    super();
    this.request = new WatchlistRequest(session);
    this.fastLinkUrl = session.getEnvironment(WatchlistEnvironment).fastLinkUrl;
    this.fastLinkConfigName = session.getEnvironment(WatchlistEnvironment).fastLinkConfigName;
    this.store = new WatchlistStore();
    this.authenticationManager = session.getManager(AuthenticationManager);
    const authenticationSubscription = this.authenticationManager.subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          if (event.auth?.user.accessType === 'anonymous') {
            this.store.clearWatchlist();
          } else {
            this.fetchWatchlist();
          }
          break;
      }
    });
    session.onPreSessionClose(() => authenticationSubscription.unsubscribe());
    if (this.authenticationManager.isLoggedIn()) {
      this.fetchWatchlist();
    }
  }

  public static getName = () => 'benzinga-watchlist';

  // avoid using this.
  public getStoredWatchlists = (): Watchlist[] => {
    return this.store.getWatchlists();
  };

  public getStoredWatchlistById = (id: WatchlistId): Watchlist | undefined => {
    return this.store.getWatchlists()?.find(w => w.watchlistId === id);
  };

  public getWatchlists = async (force = false): SafePromise<Watchlist[] | undefined> => {
    if ((force || this.store.shouldWeFetchWatchlist() === true) && this.authenticationManager.isLoggedIn()) {
      return await this.fetchWatchlist();
    } else {
      return { ok: this.store.getWatchlists() };
    }
  };

  public createWatchlist = async (
    name: string,
    settings?: Partial<Exclude<Watchlist, 'watchlistId'>> | undefined,
    symbols?: WatchlistSymbol[] | undefined,
  ): SafePromise<Watchlist> => {
    const watchlist = await this.request.createWatchlist(name, settings ?? {}, symbols ?? []);
    if (watchlist.err) {
      return watchlist as SafeType<Watchlist>;
    } else {
      this.store.addWatchlist(watchlist.ok);
    }
    return watchlist;
  };

  public addTickersToWatchlist = async (watchlist: Watchlist, tickers: StockSymbol[]): SafePromise<Watchlist> => {
    const tickersToAdd = tickers.filter(
      ticker =>
        !watchlist.symbols.some(symbol => normalizeWatchlistSymbol(symbol.symbol) === normalizeWatchlistSymbol(ticker)),
    );
    const updatedWatchlist = await this.request.addTickersToWatchlist(watchlist, tickersToAdd);
    if (updatedWatchlist.err) {
      this.fetchWatchlist();
      return updatedWatchlist as SafeType<Watchlist>;
    } else {
      const newWatchlist = this.store.addTickersToWatchlist(watchlist, updatedWatchlist.ok);
      return { ok: newWatchlist };
    }
  };

  public updateWatchlistSettings = async (watchlist: Watchlist): SafePromise<Watchlist> => {
    this.store.updateWatchlist(watchlist); // we are doing a cache optimization
    const updatedWatchlist = await this.request.updateWatchlistSettings(watchlist);
    if (updatedWatchlist.err) {
      this.getWatchlists(true); // fix our cache
      return updatedWatchlist as SafeType<Watchlist>;
    } else {
      this.store.updateWatchlist(updatedWatchlist.ok);
    }
    return updatedWatchlist;
  };

  public updateWatchlistTicker = async (watchlistSymbol: WatchlistSymbol): SafePromise<WatchlistSymbol> => {
    this.store.updateWatchlistTicker(watchlistSymbol); // we are doing a cache optimization
    const updatedWatchlistTicker = await this.request.updateWatchlistTicker(watchlistSymbol);
    if (updatedWatchlistTicker.err) {
      this.getWatchlists(true); // fix our cache
      return updatedWatchlistTicker as SafeType<WatchlistSymbol>;
    } else {
      this.store.updateWatchlistTicker(updatedWatchlistTicker.ok);
    }
    return updatedWatchlistTicker;
  };

  public removeTickersFromWatchlist = async (
    watchlist: Watchlist,
    tickers: WatchlistSymbol[],
  ): SafePromise<Watchlist> => {
    const tickersToRemove = tickers.filter(ticker => watchlist.symbols.some(symbol => symbol.symbol === ticker.symbol));
    const updatedWatchlist = await this.request.removeTickersFromWatchlist(watchlist, tickersToRemove);
    if (updatedWatchlist.err) {
      this.getWatchlists(true); // fix our cache
      return updatedWatchlist as SafeType<Watchlist>;
    } else {
      const newWatchlist = this.store.removeTickersToWatchlist(watchlist, updatedWatchlist.ok); // we are doing a cache optimization
      return { ok: newWatchlist };
    }
  };

  public removeWatchlist = async (watchlist: Watchlist): SafePromise<undefined> => {
    this.store.removeWatchlist(watchlist); // we are doing a cache optimization
    const removedWatchlist = await this.request.removeWatchlist(watchlist);
    if (removedWatchlist.err) {
      this.getWatchlists(true); // fix our cache
      return removedWatchlist as SafeType<undefined>;
    } else {
      return { ok: undefined };
    }
  };

  public syncYodleeWatchlists = async (): SafePromise<Watchlist[] | undefined> => {
    const syncedWatchlists = await this.request.syncYodleeWatchlists();
    if (syncedWatchlists.err) {
      this.getWatchlists(true); // is this still needed?
      return syncedWatchlists as SafeType<undefined>;
    } else {
      const watchlists = await this.getWatchlists(true);
      return { ok: watchlists.ok };
    }
  };

  public getYodleeAccess = async (): SafePromise<YodleeAccess | undefined> => {
    const getYodleeAccess = await this.request.getYodleeAccess();
    if (getYodleeAccess.err) {
      this.getWatchlists(true); // is this still needed?
      return getYodleeAccess as SafeType<undefined>;
    } else {
      const token = getYodleeAccess.ok.data.accessToken;
      return {
        ok: { accessToken: token, fastLinkConfigName: this.fastLinkConfigName, fastLinkUrl: this.fastLinkUrl },
      };
    }
  };
  protected onSubscribe(): WatchlistFunctions {
    return {
      addTickersToWatchlist: this.addTickersToWatchlist,
      createWatchlist: this.createWatchlist,
      getStoredWatchlistById: this.getStoredWatchlistById,
      getStoredWatchlists: this.getStoredWatchlists,
      getWatchlists: this.getWatchlists,
      removeTickersFromWatchlist: this.removeTickersFromWatchlist,
      removeWatchlist: this.removeWatchlist,
      updateWatchlistSettings: this.updateWatchlistSettings,
      updateWatchlistTicker: this.updateWatchlistTicker,
    };
  }

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }
}
